import { FC, ReactNode, useEffect, useState } from 'react';

import { Checkbox } from '../../../../components/ui/Checkbox';
import { Input } from '../../../../components/ui/Input';
import { Tabs, TabsList, TabsTrigger } from '../../../../components/ui/Tabs';
import {
    AutoPriceAdjustmentMode,
    MAX_AUTO_PRICE_MARKUP,
    MAX_AUTO_PRICE_PERCENT_ADJUSTMENT,
    MIN_AUTO_PRICE_PERCENT_ADJUSTMENT,
} from '../__shared/offerDraft';

export interface AutoPriceProgress {
    total: number;
    completed: number;
    queued: number;
    loading: number;
    priced: number;
    manual: number;
    unavailable: number;
    errors: number;
}

export interface BulkAutoPriceControls {
    enabled: boolean;
    onToggle: (enabled: boolean) => void;
    adjustment: number;
    onAdjustmentChange: (adjustment: number) => void;
    adjustmentMode: AutoPriceAdjustmentMode;
    onAdjustmentModeChange: (mode: AutoPriceAdjustmentMode) => void;
    applyToManual: boolean;
    onApplyToManualChange: (apply: boolean) => void;
    exchangeRate: string | null;
    sourceToggle: ReactNode;
    progress: AutoPriceProgress;
    onRetryFailed: () => void;
}

interface Props {
    controls: BulkAutoPriceControls;
}

type AdjustmentDirection = 'increase' | 'decrease';

const maxAdjustmentFor = (
    mode: AutoPriceAdjustmentMode,
    direction: AdjustmentDirection,
) => {
    if (mode === 'fixed') return MAX_AUTO_PRICE_MARKUP;

    return direction === 'decrease'
        ? Math.abs(MIN_AUTO_PRICE_PERCENT_ADJUSTMENT)
        : MAX_AUTO_PRICE_PERCENT_ADJUSTMENT;
};

export const BulkAutoPriceSettings: FC<Props> = ({ controls }) => {
    const [direction, setDirection] = useState<AdjustmentDirection>(
        controls.adjustment < 0 ? 'decrease' : 'increase',
    );
    const [adjustmentInput, setAdjustmentInput] = useState(
        Math.abs(controls.adjustment).toString(),
    );

    useEffect(() => {
        if (controls.adjustment !== 0) {
            setDirection(controls.adjustment < 0 ? 'decrease' : 'increase');
        }
        setAdjustmentInput(Math.abs(controls.adjustment).toString());
    }, [controls.adjustment]);

    const applyAdjustment = (
        value: number,
        nextDirection: AdjustmentDirection,
        mode = controls.adjustmentMode,
    ) => {
        const magnitude = Math.min(
            maxAdjustmentFor(mode, nextDirection),
            Math.max(0, Math.round(value)),
        );

        setAdjustmentInput(magnitude.toString());
        controls.onAdjustmentChange(
            nextDirection === 'decrease' ? -magnitude : magnitude,
        );
    };

    const handleAdjustmentInputChange = (value: string) => {
        setAdjustmentInput(value);
        if (!/^\d+$/.test(value)) return;

        const magnitude = Number(value);
        if (!Number.isFinite(magnitude)) return;

        applyAdjustment(magnitude, direction);
    };

    const handleDirectionChange = (value: string) => {
        const nextDirection = value as AdjustmentDirection;
        setDirection(nextDirection);
        applyAdjustment(Math.abs(controls.adjustment), nextDirection);
    };

    const handleModeChange = (value: string) => {
        const nextMode = value as AutoPriceAdjustmentMode;
        controls.onAdjustmentModeChange(nextMode);
        applyAdjustment(Math.abs(controls.adjustment), direction, nextMode);
    };

    return (
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            {controls.sourceToggle}
            {controls.exchangeRate && (
                <span className="text-sm whitespace-nowrap text-muted-foreground">
                    × {controls.exchangeRate} ₴
                </span>
            )}

            <Tabs
                value={direction}
                onValueChange={handleDirectionChange}
                aria-label="Напрямок корекції автоціни"
            >
                <TabsList className="grid h-9 grid-cols-2 p-0.5">
                    <TabsTrigger
                        value="increase"
                        className="h-8 px-3 text-sm"
                        aria-label="Збільшити автоціну"
                    >
                        +
                    </TabsTrigger>
                    <TabsTrigger
                        value="decrease"
                        className="h-8 px-3 text-sm"
                        aria-label="Зменшити автоціну"
                    >
                        −
                    </TabsTrigger>
                </TabsList>
            </Tabs>

            <Tabs
                value={controls.adjustmentMode}
                onValueChange={handleModeChange}
                aria-label="Тип корекції автоціни"
            >
                <TabsList className="grid h-9 grid-cols-2 p-0.5">
                    <TabsTrigger value="fixed" className="h-8 px-2.5 text-sm">
                        ₴
                    </TabsTrigger>
                    <TabsTrigger value="percent" className="h-8 px-2.5 text-sm">
                        %
                    </TabsTrigger>
                </TabsList>
            </Tabs>

            <div className="relative">
                <Input
                    type="number"
                    inputMode="numeric"
                    min="0"
                    max={maxAdjustmentFor(controls.adjustmentMode, direction)}
                    step="1"
                    aria-label="Корекція автоціни"
                    value={adjustmentInput}
                    className="h-9 w-28 pr-7 text-sm"
                    onChange={(event) =>
                        handleAdjustmentInputChange(event.target.value)
                    }
                    onBlur={() =>
                        setAdjustmentInput(
                            Math.abs(controls.adjustment).toString(),
                        )
                    }
                />
                <span className="pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 text-sm text-muted-foreground">
                    {controls.adjustmentMode === 'percent' ? '%' : '₴'}
                </span>
            </div>

            <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
                <Checkbox
                    checked={controls.applyToManual}
                    aria-label="Застосовувати корекцію до вручну змінених цін"
                    onCheckedChange={(checked) =>
                        controls.onApplyToManualChange(checked === true)
                    }
                />
                Коригувати вручну змінені автоціни
            </label>
        </div>
    );
};
